import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { notesApiService } from '../services/notesApi';
import { Ban } from '../types/player';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { AlertCircle, Globe, Server, UserCheck, UserX, Search, ChevronDown, Trash2, Shield, ShieldOff, Pencil, Gavel, Axe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BanAppealsSection } from '../components/appeals/BanAppealsSection';
import { useAuth } from '../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { PlayerProfile } from '../components/PlayerProfile';
import { CreatorProfile } from '../components/CreatorProfile';
import { cn } from '../utils/cn';
import { usernameCacheService } from '../services/usernameCache';
import Fuse from 'fuse.js';
import { EvidenceSection } from '../components/EvidenceSection';
import { LogbookCaseSection } from '../components/logbook/LogbookCaseSection';
import { TicketLinks } from '../components/TicketLinks';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { useIsMobile } from '../hooks/useIsMobile';
import { memo } from 'react';
import React from 'react';
import { EditBanModal } from '../components/EditBanModal';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';


const ScopeIcon = ({ scope, target }: { scope: Ban['scope'], target: Ban['scope_target'] }) => {
    switch (scope) {
      case 'global': return <span title={`Global: ${target || 'All'}`}><Globe className="w-4 h-4" /></span>;
      case 'cluster': return <span title={`Cluster: ${target}`}><Server className="w-4 h-4" /></span>;
      case 'fqdn': return <span title={`FQDN: ${target}`}><Server className="w-4 h-4" /></span>;
      case 'universal': return <span title={`Universal: ${target || 'All'}`}><Globe className="w-4 h-4 text-purple-400" /></span>;
      default: return null;
    }
};

const BanStatusBadge = ({ isActive, isExpired }: { isActive: boolean; isExpired: boolean }) => (
    <span className={cn("badge badge-dot", isActive ? "badge-danger" : "badge-neutral")}>
        {isActive ? 'Active' : isExpired ? 'Expired' : 'Inactive'}
    </span>
);

const AppealStatusText = ({ status }: { status: string }) => (
    <span className={cn(
        'capitalize',
        status === 'pending' || status === 'awaiting_staff' ? 'text-yellow-400' :
        status === 'approved' ? 'text-green-400' :
        status === 'denied' ? 'text-red-400' :
        'text-purple-400'
    )}>
        {status.replace('_', ' ')}
    </span>
);

const BanCardMobile = memo(({ ban, canManage, canDelete, canViewAppeals, onToggle, onDelete, onConfirmToggle, onEdit, isExpanded }: { ban: Ban, canManage: boolean, canDelete: boolean, canViewAppeals: boolean, onToggle: (id: string) => void, onDelete: (id: string) => void, onConfirmToggle: (ban: Ban) => void, onEdit: (ban: Ban) => void, isExpanded: boolean }) => {
    const isPermanent = ban.expires === null;
    const isExpired = !isPermanent && new Date((ban.expires || 0) * 1000) < new Date();
    const isActive = ban.enforced === 1 && !isExpired;

    return (
        <div className={cn("glass-card p-4", isExpanded && "border-accent-400/40")}>
            <div onClick={() => onToggle(ban.ban_uuid)} className="cursor-pointer">
                <div className="flex justify-between items-start gap-3 mb-2">
                    <PlayerProfile uuid={ban.user_uuid} />
                    <BanStatusBadge isActive={isActive} isExpired={isExpired} />
                </div>
                <p className="text-sm text-white mb-3" title={ban.reason}>{ban.reason}</p>
                <dl className="text-xs text-glass-400 space-y-1.5">
                    <div className="flex items-center gap-1.5"><dt className="font-medium text-glass-500">Banner</dt><dd className="min-w-0"><CreatorProfile creatorId={ban.banner || ban.banner_email} inline /></dd></div>
                    <div className="flex items-center gap-1.5"><dt className="font-medium text-glass-500">Expires</dt><dd className="tabular-nums">{isPermanent ? 'Permanent' : formatDistanceToNow(new Date((ban.expires || 0) * 1000), { addSuffix: true })}</dd></div>
                    <div className="flex items-center gap-1.5"><dt className="font-medium text-glass-500">Scope</dt><dd className="flex items-center gap-1.5"><ScopeIcon scope={ban.scope} target={ban.scope_target} /> <span className="font-mono">{ban.scope_target}</span></dd></div>
                    {canViewAppeals && ban.appeal_count != null && ban.appeal_count > 0 && (
                      <div className="flex items-center gap-1.5">
                        <dt className="font-medium text-glass-500 flex items-center gap-1"><Gavel className="w-3 h-3 text-purple-400" />Appeals</dt>
                        <dd>{ban.appeal_count} — {ban.latest_appeal_status && <AppealStatusText status={ban.latest_appeal_status} />}</dd>
                      </div>
                    )}
                </dl>
                <div className="flex justify-center mt-2 text-glass-500">
                    <ChevronDown className={cn("w-4 h-4 transition-transform", isExpanded && "rotate-180")} />
                </div>
            </div>
            {isExpanded && (
                <div className="border-t border-glass-200/10 pt-4 mt-3 space-y-4">
                     <TicketLinks itemId={ban.ban_uuid} itemType="ban" />
                     <EvidenceSection itemId={ban.ban_uuid} itemType="ban" />
                     <LogbookCaseSection itemType="ban" itemId={ban.ban_uuid} subjectUuid={ban.user_uuid} />
                     {canViewAppeals && ban.appealable === 1 && (
                       <BanAppealsSection banId={ban.ban_uuid} />
                     )}
                     <div className="flex flex-wrap justify-end gap-2 mt-2">
                        {canManage && (
                            <>
                                <button onClick={() => onEdit(ban)} className="glass-button-secondary btn-xs">
                                    <Pencil className="w-3.5 h-3.5" />
                                    Edit
                                </button>
                                <button onClick={() => onConfirmToggle(ban)} className="glass-button-secondary btn-xs">
                                    {ban.enforced === 1 ? <ShieldOff className="w-3.5 h-3.5" /> : <Shield className="w-3.5 h-3.5" />}
                                    {ban.enforced === 1 ? 'Lift Ban' : 'Re-enforce'}
                                </button>
                            </>
                        )}
                        {canDelete && (
                            <button onClick={() => onDelete(ban.ban_uuid)} className="btn-danger btn-xs">
                                <Trash2 className="w-3.5 h-3.5" />
                                Delete
                            </button>
                        )}
                     </div>
                </div>
            )}
        </div>
    );
});

export function BansView() {
  useDocumentTitle('Ban Management');
  const { identity } = useAuth();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();

  const [allBans, setAllBans] = useState<Ban[]>([]);
  const [filteredBans, setFilteredBans] = useState<Ban[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [banForConfirmation, setBanForConfirmation] = useState<Ban | null>(null);
  const [banForDeletion, setBanForDeletion] = useState<Ban | null>(null);
  const [banForEditing, setBanForEditing] = useState<Ban | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isUpdatingReason, setIsUpdatingReason] = useState(false);
  const [visibleCount, setVisibleCount] = useState(50);
  const observer = useRef<IntersectionObserver | null>(null);
  const highlightRef = useRef<HTMLTableRowElement | HTMLDivElement | null>(null);
  const highlightProcessed = useRef(false);

  const canManageBans = useMemo(() => {
    if (!identity) return false;
    const groups = new Set(identity.raw.custom.groups);
    return groups.has('SPY_Bans_Admin') || groups.has('SPY_Bans_Superadmins') || groups.has('SPY_Bans_Superadmin');
  }, [identity]);

  const canDeleteBans = useMemo(() => {
    if (!identity) return false;
    const groups = new Set(identity.raw.custom.groups);
    return groups.has('SPY_Bans_Superadmins') || groups.has('SPY_Bans_Superadmin');
  }, [identity]);

  const canViewAppeals = useMemo(() => {
    if (!identity) return false;
    const groups = new Set(identity.raw.custom.groups);
    return groups.has('SPY_Bans_Helper') || groups.has('SPY_Bans_Mod') || groups.has('SPY_Bans_Admin') || groups.has('SPY_Bans_Superadmins') || groups.has('SPY_Super_admin');
  }, [identity]);

  const fetchBans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await notesApiService.getAllBans(1, 10000); // Fetch all
      const allFetchedBans = response.results;

      setAllBans(allFetchedBans);

      const uuidsToFetch = new Set<string>();
      allFetchedBans.forEach(ban => {
          uuidsToFetch.add(ban.user_uuid);
          if (ban.banner) uuidsToFetch.add(ban.banner);
          if (ban.banner_email) uuidsToFetch.add(ban.banner_email);
      });
      usernameCacheService.prefetchUsernames(Array.from(uuidsToFetch));
    } catch (err) {
      setError('Failed to fetch bans.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBans();
  }, [fetchBans]);

  // Handle highlight query parameter (e.g., from Evidence Explorer navigation)
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    if (highlightId && !loading && allBans.length > 0 && !highlightProcessed.current) {
      highlightProcessed.current = true;
      setExpandedRow(highlightId);
      // Clear the highlight param from URL
      setSearchParams({}, { replace: true });
      // Scroll to highlighted row after a brief delay to allow DOM rendering
      setTimeout(() => {
        if (highlightRef.current) {
          highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  }, [searchParams, loading, allBans, setSearchParams]);

  const handleLoadMore = () => {
    setVisibleCount(prevCount => prevCount + 50);
  };

  const lastBanElementRef = useCallback((node: HTMLTableRowElement | HTMLDivElement | null) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && visibleCount < allBans.length) {
        handleLoadMore();
      }
    });

    if (node) observer.current.observe(node);
  }, [loading, visibleCount, allBans.length]);

  const handleDeleteBan = async () => {
    if (!banForDeletion) return;
    try {
        await notesApiService.deleteBan(banForDeletion.ban_uuid);
        setAllBans(prev => prev.filter(b => b.ban_uuid !== banForDeletion.ban_uuid));
        setFilteredBans(prev => prev.filter(b => b.ban_uuid !== banForDeletion.ban_uuid));
    } catch (err) {
        alert('Failed to delete ban.');
    } finally {
        setBanForDeletion(null);
    }
  };

  const handleEditBan = async (updatedReason: string, updatedExpires: number | null) => {
    if (!banForEditing) return;
    setIsUpdatingReason(true);
    try {
        const updatedBan = await notesApiService.updateBan(banForEditing.ban_uuid, {
            reason: updatedReason,
            expires: updatedExpires,
        });
        setAllBans(prev => prev.map(b => b.ban_uuid === updatedBan.ban_uuid ? updatedBan : b));
        setFilteredBans(prev => prev.map(b => b.ban_uuid === updatedBan.ban_uuid ? updatedBan : b));
    } catch (err) {
        alert('Failed to update ban reason.');
    } finally {
        setIsUpdatingReason(false);
        setBanForEditing(null);
    }
  };

  const handleToggleEnforced = async () => {
    if (!banForConfirmation) return;

    setIsUpdatingStatus(true);
    try {
      const updatedBan = await notesApiService.updateBan(banForConfirmation.ban_uuid, {
        enforced: banForConfirmation.enforced === 1 ? 0 : 1,
      });
      setAllBans(prev => prev.map(b => b.ban_uuid === updatedBan.ban_uuid ? updatedBan : b));
      setFilteredBans(prev => prev.map(b => b.ban_uuid === updatedBan.ban_uuid ? updatedBan : b));
    } catch (err) {
      alert('Failed to update ban status.');
    } finally {
        setIsUpdatingStatus(false);
        setBanForConfirmation(null);
    }
  };

  const handleToggleRow = (banUuid: string) => {
    setExpandedRow(current => (current === banUuid ? null : banUuid));
  };

  const fuse = useMemo(() => {
    return new Fuse(allBans, {
      keys: ['user_uuid', 'reason', 'banner', 'banner_email'],
      includeScore: true,
      threshold: 0.4,
      getFn: (obj, path) => {
          const value = Fuse.config.getFn(obj, path);
          if (path[0] === 'user_uuid' || path[0] === 'banner') {
              return usernameCacheService.getName(value as string) || value;
          }
          return value;
      }
    });
  }, [allBans]);

  useEffect(() => {
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery === '') {
      setFilteredBans(allBans.slice(0, visibleCount));
    } else {
      const results = fuse.search(trimmedQuery);
      setFilteredBans(results.map(result => result.item));
    }
  }, [searchQuery, allBans, fuse, visibleCount]);

  const activeCount = useMemo(() => allBans.filter(ban => {
    const isPermanent = ban.expires === null;
    const isExpired = !isPermanent && new Date((ban.expires || 0) * 1000) < new Date();
    return ban.enforced === 1 && !isExpired;
  }).length, [allBans]);

  const renderDesktopView = () => (
    <div className="table-wrap">
        <table className="table">
            <thead>
                <tr>
                    <th className="th">Status</th>
                    <th className="th">Player</th>
                    <th className="th">Reason</th>
                    <th className="th">Banner</th>
                    <th className="th">Expires</th>
                    <th className="th">Scope</th>
                    <th className="th">Appealable</th>
                    {canViewAppeals && <th className="th">Appeals</th>}
                    <th className="th text-right">Actions</th>
                </tr>
            </thead>
            <tbody>
                {filteredBans.map((ban, index) => {
                    const isPermanent = ban.expires === null;
                    const isExpired = !isPermanent && new Date((ban.expires || 0) * 1000) < new Date();
                    const isActive = ban.enforced === 1 && !isExpired;
                    const isExpanded = expandedRow === ban.ban_uuid;

                    return (
                        <React.Fragment key={ban.ban_uuid}>
                            <tr ref={(node) => {
                              if (index === filteredBans.length - 1) lastBanElementRef(node);
                              if (expandedRow === ban.ban_uuid) {
                                highlightRef.current = node;
                              }
                            }} className={cn("cursor-pointer", isExpanded && "row-active")} onClick={() => handleToggleRow(ban.ban_uuid)}>
                                <td className="td">
                                    <BanStatusBadge isActive={isActive} isExpired={isExpired} />
                                </td>
                                <td className="td min-w-[200px]"><PlayerProfile uuid={ban.user_uuid} /></td>
                                <td className="td text-white max-w-xs truncate" title={ban.reason}>{ban.reason}</td>
                                <td className="td">
                                    <CreatorProfile creatorId={ban.banner || ban.banner_email} />
                                </td>
                                <td className="td text-glass-300 tabular-nums whitespace-nowrap">{isPermanent ? 'Permanent' : formatDistanceToNow(new Date((ban.expires || 0) * 1000), { addSuffix: true })}</td>
                                <td className="td">
                                    <div className="flex items-center gap-2 text-glass-400">
                                        <ScopeIcon scope={ban.scope} target={ban.scope_target} />
                                        {(ban.scope_target) && <span className="text-glass-300 font-mono text-xs">{ban.scope_target}</span>}
                                    </div>
                                </td>
                                <td className="td">
                                    {ban.appealable ? <UserCheck className="w-4 h-4 text-green-400" /> : <UserX className="w-4 h-4 text-red-400" />}
                                </td>
                                {canViewAppeals && (
                                <td className="td">
                                    {ban.appeal_count && ban.appeal_count > 0 ? (
                                      <Link
                                        to={`/appeals/${ban.latest_appeal_id}`}
                                        onClick={(e) => e.stopPropagation()}
                                        className="badge badge-neutral hover:border-glass-200/30 hover:text-white"
                                        title={`${ban.appeal_count} appeal(s) — latest: ${ban.latest_appeal_status}`}
                                      >
                                        <Gavel className="w-3 h-3" />
                                        <span>{ban.appeal_count}</span>
                                        {ban.latest_appeal_status && <AppealStatusText status={ban.latest_appeal_status} />}
                                      </Link>
                                    ) : (
                                      <span className="text-glass-500 text-xs">—</span>
                                    )}
                                </td>
                                )}
                                <td className="td">
                                  <div className="flex items-center justify-end gap-0.5">
                                    {canManageBans && (
                                        <>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setBanForEditing(ban);
                                            }}
                                            className="btn-icon btn-icon-sm"
                                            title="Edit Ban Reason"
                                            aria-label="Edit Ban Reason"
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setBanForConfirmation(ban);
                                            }}
                                            className="btn-icon btn-icon-sm text-blue-400 hover:text-blue-300"
                                            title={isActive ? "Lift Ban" : "Re-enforce Ban"}
                                            aria-label={isActive ? "Lift Ban" : "Re-enforce Ban"}
                                        >
                                            {isActive ? <ShieldOff className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                                        </button>
                                        </>
                                    )}
                                    {canDeleteBans && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation(); // prevent row from toggling
                                                setBanForDeletion(ban);
                                            }}
                                            className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                                            title="Delete Ban"
                                            aria-label="Delete Ban"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                    <ChevronDown className={cn("w-4 h-4 text-glass-500 ml-1 transition-transform", isExpanded && "rotate-180")} />
                                  </div>
                                </td>
                            </tr>
                            {isExpanded && (
                                <tr className="row-detail">
                                    <td className="td" colSpan={canViewAppeals ? 9 : 8}>
                                        <div className="space-y-4 py-1">
                                            <TicketLinks itemId={ban.ban_uuid} itemType="ban" />
                                            <EvidenceSection itemId={ban.ban_uuid} itemType="ban" />
                                            <LogbookCaseSection itemType="ban" itemId={ban.ban_uuid} subjectUuid={ban.user_uuid} />
                                            {canViewAppeals && ban.appealable === 1 && (
                                              <BanAppealsSection banId={ban.ban_uuid} />
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    );
                })}
            </tbody>
        </table>
        {loading && <div className="text-center p-4 text-sm text-glass-400">Loading bans...</div>}
      </div>
  );

  const renderMobileView = () => (
    <div className="space-y-3">
        {filteredBans.map((ban, index) => {
            const isExpanded = expandedRow === ban.ban_uuid;
            return (
                <div key={ban.ban_uuid} ref={(node) => {
                  if (index === filteredBans.length - 1) lastBanElementRef(node);
                  if (expandedRow === ban.ban_uuid) {
                    highlightRef.current = node;
                  }
                }}>
                    <BanCardMobile
                        ban={ban}
                        canManage={canManageBans}
                        canDelete={canDeleteBans}
                        canViewAppeals={canViewAppeals}
                        onToggle={handleToggleRow}
                        onDelete={handleDeleteBan}
                        onConfirmToggle={setBanForConfirmation}
                        onEdit={setBanForEditing}
                        isExpanded={isExpanded}
                    />

                </div>
            );
        })}
        {loading && <div className="text-center p-4 text-sm text-glass-400">Loading bans...</div>}
    </div>
  );

  const renderLoading = () => (
    <div className="table-wrap p-4 space-y-3">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <SkeletonLoader className="h-5 w-16 rounded-full" />
          <SkeletonLoader className="h-8 w-8 rounded-md" />
          <SkeletonLoader className="h-4 w-40" />
          <SkeletonLoader className="h-4 flex-1" />
          <SkeletonLoader className="h-4 w-24" />
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Moderation"
        title="Ban Management"
        description="View all bans across the network."
        icon={Axe}
        meta={!loading && !error ? (
          <>
            <span className="badge badge-neutral">{allBans.length.toLocaleString()} total</span>
            <span className="badge badge-danger badge-dot">{activeCount.toLocaleString()} active</span>
          </>
        ) : undefined}
      >
        {/* TODO: Add 'Create Ban' button */}
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
              <input
                type="text"
                placeholder="Search by player, banner, or reason…"
                className="glass-input w-full pl-9"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
          </div>
          {!loading && !error && (
            <span className="text-xs text-glass-400 tabular-nums md:text-right">
              {searchQuery.trim()
                ? `${filteredBans.length.toLocaleString()} match${filteredBans.length === 1 ? '' : 'es'}`
                : `Showing ${Math.min(visibleCount, allBans.length).toLocaleString()} of ${allBans.length.toLocaleString()}`}
            </span>
          )}
        </div>
      </PageHeader>

      {loading && renderLoading()}
      {error && (
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to load bans"
          description={error}
          action={<button onClick={fetchBans} className="glass-button">Retry</button>}
        />
      )}

      {!loading && !error && filteredBans.length === 0 && (
        <EmptyState
          icon={searchQuery.trim() ? Search : Axe}
          title={searchQuery.trim() ? 'No bans match your search' : 'No bans recorded'}
          description={searchQuery.trim() ? 'Try a different player name, banner or reason.' : 'Bans issued across the network will appear here.'}
          action={searchQuery.trim() ? <button onClick={() => setSearchQuery('')} className="glass-button-secondary">Clear search</button> : undefined}
        />
      )}

      {!loading && !error && filteredBans.length > 0 && (isMobile ? renderMobileView() : renderDesktopView())}

      {banForConfirmation && (
          <ConfirmationModal
              title={banForConfirmation.enforced === 1 ? 'Confirm Ban Lift' : 'Confirm Ban Re-enforcement'}
              message={`Are you sure you want to ${banForConfirmation.enforced === 1 ? 'lift' : 're-enforce'} this ban?`}
              confirmText={banForConfirmation.enforced === 1 ? 'Lift Ban' : 'Re-enforce'}
              onConfirm={handleToggleEnforced}
              onClose={() => setBanForConfirmation(null)}
              isLoading={isUpdatingStatus}
          />
      )}
      {banForDeletion && (
          <ConfirmationModal
              title="Confirm Ban Deletion"
              message="Are you sure you want to permanently delete this ban? This action cannot be undone and should only be used for bans made by mistake. For appealed bans, please use the 'Lift Ban' option."
              confirmText="Yes, Delete Ban"
              onConfirm={handleDeleteBan}
              onClose={() => setBanForDeletion(null)}
              isLoading={false}
          />
      )}
      {banForEditing && (
          <EditBanModal
              ban={banForEditing}
              onClose={() => setBanForEditing(null)}
              onSave={handleEditBan}
              isLoading={isUpdatingReason}
          />
      )}
    </div>
  );
}
